import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { AhmedElshahatJobService } from '../../../../services/ahmed-elshahat-job';
import { JobApplicationService } from '../../../../services/job-application';


@Component({
  selector: 'app-jobsdetails',
  templateUrl: './jobsdetails.component.html',
  styleUrl: './jobsdetails.component.scss'
})
export class JobsdetailsComponent implements OnInit {
  contentReady = false;
  isLoading = false;
  isAr = false;

  job: any = null;
  errorMessage = '';

  applyModalOpen = false;
  directApplyingJobId: number | string | null = null;
  applyMessage = '';
  applyMessageType: 'success' | 'error' = 'success';
  appliedJobIds = new Set<string>();

  private readonly employmentTypeOptions = [
    { value: 'full_time', labelEn: 'Full Time', labelAr: 'دوام كامل' },
    { value: 'part_time', labelEn: 'Part Time', labelAr: 'دوام جزئي' },
    { value: 'contract', labelEn: 'Contract', labelAr: 'عقد' },
    { value: 'freelance', labelEn: 'Freelance', labelAr: 'فريلانس' },
    { value: 'internship', labelEn: 'Internship', labelAr: 'تدريب' }
  ];

  private readonly workModelOptions = [
    { value: 'on_site', labelEn: 'On-site', labelAr: 'من مقر العمل' },
    { value: 'onsite', labelEn: 'On-site', labelAr: 'من مقر العمل' },
    { value: 'remote', labelEn: 'Remote', labelAr: 'عن بُعد' },
    { value: 'hybrid', labelEn: 'Hybrid', labelAr: 'هايبرد' }
  ];

  private readonly statusOptions = [
    { value: 'published', labelEn: 'Published', labelAr: 'منشورة' },
    { value: 'active', labelEn: 'Active', labelAr: 'متاحة' },
    { value: 'pending', labelEn: 'Pending', labelAr: 'قيد المراجعة' },
    { value: 'closed', labelEn: 'Closed', labelAr: 'مغلقة' }
  ];

  private readonly requirementTypeOptions = [
    { value: 'skill', labelEn: 'Skill', labelAr: 'مهارة' },
    { value: 'experience', labelEn: 'Experience', labelAr: 'خبرة' },
    { value: 'education', labelEn: 'Education', labelAr: 'تعليم' },
    { value: 'language', labelEn: 'Language', labelAr: 'لغة' },
    { value: 'certificate', labelEn: 'Certificate', labelAr: 'شهادة' },
    { value: 'other', labelEn: 'Other', labelAr: 'أخرى' }
  ];

  private readonly translations: any = {
    en: {
      loading: 'Loading job details...',
      backToJobs: 'Back to jobs',
      applyNow: 'Apply Now',
      salary: 'Salary',
      employmentType: 'Employment Type',
      workModel: 'Work Model',
      postedAt: 'Posted At',
      views: 'Views',
      examRequired: 'Exam Required',
      personalityAssessment: 'Personality Assessment',
      yes: 'Yes',
      no: 'No',
      description: 'Job Description',
      responsibilities: 'Responsibilities',
      skills: 'Skills',
      requirements: 'Requirements',
      required: 'Required',
      notSpecified: 'Not specified',
      negotiable: 'Negotiable',
      jobNotFound: 'Job not found',
      jobNotFoundText: 'This job may have been removed or is no longer available.',
      applyTitle: 'Apply Now',
      directApplySuccess: 'Application submitted successfully',
      alreadyApplied: 'You have already applied for this job',
      directApplyError: 'Could not apply right now. Please try again.'
    },
    ar: {
      loading: 'جاري تحميل تفاصيل الوظيفة...',
      backToJobs: 'الرجوع للوظائف',
      applyNow: 'قدّم الآن',
      salary: 'الراتب',
      employmentType: 'نوع الوظيفة',
      workModel: 'نظام العمل',
      postedAt: 'تاريخ النشر',
      views: 'المشاهدات',
      examRequired: 'اختبار مطلوب',
      personalityAssessment: 'تقييم شخصية',
      yes: 'نعم',
      no: 'لا',
      description: 'وصف الوظيفة',
      responsibilities: 'المسؤوليات',
      skills: 'المهارات',
      requirements: 'المتطلبات',
      required: 'مطلوب',
      notSpecified: 'غير محدد',
      negotiable: 'قابل للتفاوض',
      jobNotFound: 'الوظيفة غير موجودة',
      jobNotFoundText: 'قد تكون الوظيفة اتحذفت أو لم تعد متاحة.',
      applyTitle: 'قدّم على الوظيفة'
    }
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private jobService: AhmedElshahatJobService,
    private applicationService: JobApplicationService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.detectLanguage();
    this.loadAppliedJobIds();

    this.route.paramMap.subscribe(params => {
      const id = params.get('id');

      if (!id) {
        this.showNotFound();
        return;
      }

      this.loadJob(id);
    });
  }

  private detectLanguage(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const savedLang =
      localStorage.getItem('lang') ||
      localStorage.getItem('language') ||
      localStorage.getItem('locale') ||
      this.document.documentElement.lang ||
      'en';

    this.isAr =
      savedLang.toLowerCase().startsWith('ar') ||
      this.document.documentElement.dir === 'rtl';
  }

  private loadJob(id: string): void {
    this.isLoading = true;
    this.contentReady = false;
    this.errorMessage = '';

    this.jobService.getJob(id).subscribe({
      next: (res: any) => {
        this.job = this.normalizeJobResponse(res);

        if (!this.job) {
          this.showNotFound();
          return;
        }

        this.isLoading = false;
        this.contentReady = true;
      },
      error: () => {
        this.showNotFound();
      }
    });
  }

  private normalizeJobResponse(res: any): any {
    return res?.data?.data || res?.data || res?.job || res || null;
  }

  private showNotFound(): void {
    this.job = null;
    this.isLoading = false;
    this.contentReady = true;
    this.errorMessage = this.t('jobNotFoundText');
  }

  t(key: string): string {
    const lang = this.isAr ? 'ar' : 'en';
    return this.translations[lang][key] || key;
  }

  goBack(): void {
    this.location.back();
  }

  goToJobs(): void {
    this.router.navigate(['/jobs']);
  }

  openApplyModal(): void {
    if (this.isCompanyUser()) {
      return;
    }

    if (!this.job?.id) {
      return;
    }

    if (this.isJobApplied(this.job)) {
      return;
    }

    if (this.getApplicationFormType(this.job) === 'doctor') {
      this.router.navigate(['/apply/doctor', this.job.id]);
      return;
    }

    if (this.getApplicationFormType(this.job) === 'dentist') {
      this.router.navigate(['/apply/dentist', this.job.id]);
      return;
    }

    if (this.getApplicationFormType(this.job) === 'general' && this.tryDirectApply(this.job)) {
      return;
    }

    this.applyModalOpen = true;
  }

  closeApplyModal(): void {
    this.applyModalOpen = false;
  }

  handleApplicationSubmitted(): void {
    if (this.job?.id) {
      this.appliedJobIds.add(String(this.job.id));
    }

    this.closeApplyModal();
  }

  isJobApplied(job: any): boolean {
    return Boolean(job?.id && this.appliedJobIds.has(String(job.id)));
  }

  isCompanyUser(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const directType =
      localStorage.getItem('user_type') ||
      localStorage.getItem('account_type') ||
      localStorage.getItem('role') ||
      localStorage.getItem('user_role') ||
      localStorage.getItem('type');

    if (this.normalizeUserType(directType) === 'company') {
      return true;
    }

    try {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;

      return this.normalizeUserType(
        user?.user_type ||
        user?.account_type ||
        user?.role ||
        user?.user_role ||
        user?.type ||
        user?.user?.user_type
      ) === 'company';
    } catch {
      return false;
    }
  }

  getApplyButtonLabel(): string {
    if (this.isJobApplied(this.job)) {
      return this.isAr ? 'تم التقديم' : 'Applied';
    }

    if (this.directApplyingJobId === this.job?.id) {
      return this.isAr ? 'جاري التقديم...' : 'Applying...';
    }

    return this.t('applyNow');
  }

  private tryDirectApply(job: any): boolean {
    const user = this.getStoredUserWithCv();

    if (!user?.id || !job?.id) {
      return false;
    }

    if (this.directApplyingJobId) {
      return true;
    }

    this.directApplyingJobId = job.id;
    this.applyMessage = '';

    this.applicationService
      .applyToJob({
        job_id: job.id,
        user_id: user.id
      })
      .subscribe({
        next: () => {
          this.appliedJobIds.add(String(job.id));
          this.directApplyingJobId = null;
          this.applyModalOpen = false;
          this.showApplyMessage(this.directApplySuccessText(), 'success');
        },
        error: (err: any) => {
          if (this.isAlreadyAppliedError(err)) {
            this.appliedJobIds.add(String(job.id));
          }

          this.directApplyingJobId = null;
          this.showApplyMessage(this.getDirectApplyErrorText(err), 'error');
        }
      });

    return true;
  }

  private getStoredUserWithCv(): any | null {
    if (this.isCompanyUser()) {
      return null;
    }

    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : {};
      const userId = user?.id || localStorage.getItem('user_id');
      const cv =
        user?.user_cv ||
        user?.user_cv_url ||
        user?.cv ||
        localStorage.getItem('user_cv') ||
        localStorage.getItem('user_cv_url');

      return userId && cv ? { ...user, id: userId, user_cv: cv } : null;
    } catch {
      return null;
    }
  }

  private loadAppliedJobIds(): void {
    const userId = this.getStoredUserId();

    if (!userId) {
      return;
    }

    this.applicationService.getApplicationsByUserId(userId).subscribe({
      next: (res: any) => {
        this.appliedJobIds = new Set(
          this.extractApplications(res)
            .map((application: any) => this.getApplicationJobId(application))
            .filter((jobId: any) => jobId !== null && jobId !== undefined)
            .map((jobId: any) => String(jobId))
        );
      },
      error: () => {
        this.appliedJobIds.clear();
      }
    });
  }

  private getStoredUserId(): number | string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : {};

      return user?.id || localStorage.getItem('user_id') || null;
    } catch {
      return localStorage.getItem('user_id') || null;
    }
  }

  private extractApplications(res: any): any[] {
    const payload = res?.data ?? res?.applications ?? res;

    if (Array.isArray(payload?.data)) {
      return payload.data;
    }

    if (Array.isArray(payload)) {
      return payload;
    }

    return [];
  }

  private getApplicationJobId(application: any): number | string | null {
    return (
      application?.job_id ||
      application?.job?.id ||
      application?.ahmed_elshahat_job?.id ||
      application?.job_data?.id ||
      null
    );
  }

  private directApplySuccessText(): string {
    return this.isAr
      ? 'تم التقديم على الوظيفة بنجاح'
      : this.t('directApplySuccess');
  }

  private getDirectApplyErrorText(err: any): string {
    const alreadyApplied =
      err?.status === 409 || err?.status === 422
        ? this.isAr
          ? 'لقد قدمت على هذه الوظيفة من قبل'
          : this.t('alreadyApplied')
        : '';

    return (
      err?.error?.message ||
      err?.error?.error ||
      alreadyApplied ||
      (this.isAr ? 'تعذر التقديم الآن. حاول مرة أخرى.' : this.t('directApplyError'))
    );
  }

  private isAlreadyAppliedError(err: any): boolean {
    return err?.status === 409 || err?.status === 422;
  }

  private normalizeUserType(value: any): string {
    const type = String(value || '').toLowerCase().trim();

    if (
      type === 'company' ||
      type === 'companies' ||
      type === 'employer' ||
      type === 'employers' ||
      type === 'business'
    ) {
      return 'company';
    }

    return type;
  }

  private showApplyMessage(message: string, type: 'success' | 'error'): void {
    this.applyMessage = message;
    this.applyMessageType = type;
  }

  getJobTitle(job: any): string {
    return this.pickLang(job?.title_en, job?.title_ar) || this.t('notSpecified');
  }

  getCompanyName(job: any): string {
    const company = job?.company || job?.company_data;

    return (
      this.pickLang(
        company?.name_en || company?.name,
        company?.name_ar
      ) ||
      job?.company_name ||
      this.t('notSpecified')
    );
  }

  getCityName(job: any): string {
    const city = job?.city;

    return (
      this.pickLang(
        city?.city_name_en || city?.name_en || city?.name,
        city?.city_name_ar || city?.name_ar
      ) ||
      this.pickLang(job?.city_name_en, job?.city_name_ar) ||
      this.t('notSpecified')
    );
  }

  getCountryName(job: any): string {
    const country = job?.country || job?.city?.country;

    return (
      this.pickLang(
        country?.country_name_en || country?.name_en || country?.name,
        country?.country_name_ar || country?.name_ar
      ) ||
      this.pickLang(job?.country_name_en, job?.country_name_ar) ||
      this.t('notSpecified')
    );
  }

  getSalary(job: any): string {
    if (!job) {
      return this.t('notSpecified');
    }

    if (job.salary_type === 'negotiable') {
      return job.salary_note || this.t('negotiable');
    }

    if (job.salary_type === 'hidden') {
      return this.t('notSpecified');
    }

    const currency = job.salary_currency || 'EGP';
    const min = Number(job.salary_min || 0);
    const max = Number(job.salary_max || 0);

    if (min && max) {
      return `${this.formatNumber(min)} - ${this.formatNumber(max)} ${currency}`;
    }

    if (min) {
      return `${this.formatNumber(min)} ${currency}`;
    }

    if (max) {
      return `${this.formatNumber(max)} ${currency}`;
    }

    return job.salary_note || this.t('notSpecified');
  }

  getJobType(job: any): string {
    return this.getOptionLabel(this.employmentTypeOptions, job?.employment_type);
  }

  getApplicationFormType(job: any): 'general' | 'doctor' | 'dentist' {
    const jobType = String(job?.job_type || '').trim().toLowerCase();

    if (jobType === 'fordoctors') return 'doctor';
    if (jobType === 'fordentist') return 'dentist';

    return 'general';
  }

  getWorkModel(job: any): string {
    return this.getOptionLabel(this.workModelOptions, job?.work_model);
  }

  getStatusLabel(status: string): string {
    return this.getOptionLabel(this.statusOptions, status || 'published');
  }

  getRequirementTypeLabel(type: string): string {
    return this.getOptionLabel(this.requirementTypeOptions, type || 'other');
  }

  private getOptionLabel(options: any[], value: string): string {
    const option = options.find(item => item.value === value);

    if (!option) {
      return value || this.t('notSpecified');
    }

    return this.isAr ? option.labelAr : option.labelEn;
  }

  getPostedAt(job: any): string {
    const dateValue = job?.posted_at || job?.created_at;

    if (!dateValue) {
      return this.t('notSpecified');
    }

    try {
      return new Intl.DateTimeFormat(this.isAr ? 'ar-EG' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(new Date(dateValue));
    } catch {
      return dateValue;
    }
  }

  getJobDescription(job: any): string {
    return (
      this.pickLang(job?.description_en, job?.description_ar) ||
      job?.description ||
      ''
    );
  }

  getJobResponsibilities(job: any): string {
    return (
      this.pickLang(job?.responsibilities_en, job?.responsibilities_ar) ||
      job?.responsibilities ||
      ''
    );
  }

  getTags(job: any): string[] {
    const result: string[] = [];

    const skills = job?.skills || job?.job_skills || [];
    const tags = job?.tags;

    if (Array.isArray(skills)) {
      skills.forEach((item: any) => {
        if (typeof item === 'string') {
          result.push(item);
          return;
        }

        const skill = item?.skill || item;

        const label =
          this.pickLang(
            skill?.name_en || skill?.skill_name_en || skill?.title_en || skill?.name,
            skill?.name_ar || skill?.skill_name_ar || skill?.title_ar
          ) ||
          item?.name ||
          item?.title;

        if (label) {
          result.push(label);
        }
      });
    }

    if (Array.isArray(tags)) {
      result.push(...tags.filter(Boolean));
    }

    if (typeof tags === 'string') {
      result.push(
        ...tags
          .split(',')
          .map(tag => tag.trim())
          .filter(Boolean)
      );
    }

    return Array.from(new Set(result));
  }

  getRequirements(job: any): any[] {
    const requirements =
      job?.requirements ||
      job?.job_requirements ||
      job?.jobRequirements ||
      [];

    return Array.isArray(requirements) ? requirements : [];
  }

  getRequirementText(req: any): string {
    return (
      this.pickLang(
        req?.requirement_text_en || req?.text_en || req?.title_en,
        req?.requirement_text_ar || req?.text_ar || req?.title_ar
      ) ||
      req?.requirement_text ||
      req?.text ||
      req?.title ||
      ''
    );
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat(this.isAr ? 'ar-EG' : 'en-US').format(
      Number(value || 0)
    );
  }

  private pickLang(enValue?: string, arValue?: string): string {
    if (this.isAr) {
      return arValue || enValue || '';
    }

    return enValue || arValue || '';
  }

  trackByRequirementId(index: number, item: any): number {
    return item?.id || index;
  }
}
